import { ArticleResolution, FeatureRequestResolutions } from '@wix/answers-api';
import { Tag, Text, Tooltip } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import Long from 'long';
import { FunctionComponent } from 'react';
import { DATA_HOOKS } from '../../dataHooks';
import { statusToClass } from '../../utils/roadmap';
import css from './index.module.scss';

type RoadmapResolutionBadgeProps = {
  resolution: ArticleResolution;
  size: 'tiny' | 'small' | 'medium';
  resolutionEta?: Long | number | null;
  showTooltip?: boolean;
  onClickStatus?: (
    articleResolution: ArticleResolution,
    clickedText: string
  ) => void;
};

export const RoadmapResolutionBadge: FunctionComponent<
  RoadmapResolutionBadgeProps
> = ({ resolution, size, resolutionEta, onClickStatus, showTooltip }) => {
  const { t } = useTranslation();

  const resolutionClass =
    statusToClass[
      (resolution as unknown as FeatureRequestResolutions) ||
        ArticleResolution.COLLECTING_VOTES
    ];

  const launchedTagText = (eta: Long | number) => {
    const mum =
      typeof eta === 'number' ? eta : new Long(eta.low, eta.high).toNumber();
    const date = new Date(mum);
    const month = date.getUTCMonth();
    const monthText = t(`month.${month}`);
    const year = date.getUTCFullYear();
    return t('roadmap.article-list.resolutions.launched', {
      month: monthText,
      year,
    });
  };

  const articleItemStatus =
    resolution === ArticleResolution.RELEASED && resolutionEta
      ? launchedTagText(resolutionEta)
      : t(`roadmap.article-list.resolutions.${resolution}`);

  const renderStatusBody = () => (
    <div className={`${css.statusBody}`}>
      <Text
        dataHook={DATA_HOOKS.ARTICLE_ITEM_STATUS}
        datatype={`${resolution}`}
        size={size}
        className={css.resolutionText}
      >
        {articleItemStatus}
      </Text>
    </div>
  );

  return (
    <div
      className={`${css.roadmapResolutionBadge} ${
        onClickStatus ? 'clickable' : ''
      }`}
    >
      <Tooltip
        size="small"
        disabled={showTooltip}
        content={t(`roadmap.article-list.resolution.tooltip.${resolution}`)}
      >
        <Tag
          size={size}
          className={`${css.statusTag} ${css[resolutionClass]}`}
          id={`${resolution}`}
          theme="warning"
          dataHook={DATA_HOOKS.ROADMAP_RESOLUTION_BADGE}
          removable={false}
          onClick={
            onClickStatus
              ? () => onClickStatus(resolution, articleItemStatus)
              : undefined
          }
        >
          {renderStatusBody()}
        </Tag>
      </Tooltip>
    </div>
  );
};
